import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search } from 'lucide-react';
import '../styles/Blog.css';
import { BlogHero } from '../components/PageHeroes';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const blogPosts = [
    {
      id: 1,
      title: 'HMRC Filing Guide 2025',
      author: 'Sarah Johnson',
      date: 'January 15, 2025',
      category: 'Compliance',
      excerpt: 'Complete guide to HMRC RTI filing for 2025. Learn everything you need to know about filing deadlines, requirements, and best practices.',
      image: 'hmrc-guide',
      readTime: '8 min read'
    },
    {
      id: 2,
      title: 'Payroll Best Practices Guide',
      author: 'Michael Chen',
      date: 'January 10, 2025',
      category: 'Management',
      excerpt: 'Tips and strategies for efficient payroll management. Discover how top businesses streamline their payroll processes.',
      image: 'payroll-practices',
      readTime: '6 min read'
    },
    {
      id: 3,
      title: 'GDPR Compliance for Payroll',
      author: 'Emma Davis',
      date: 'January 5, 2025',
      category: 'Security',
      excerpt: 'Protect your payroll data with our comprehensive GDPR compliance guide. Everything you need to know about data protection.',
      image: 'gdpr-guide',
      readTime: '10 min read'
    },
    {
      id: 4,
      title: 'Scaling Payroll Operations',
      author: 'James Wilson',
      date: 'December 28, 2024',
      category: 'Growth',
      excerpt: 'How to scale your payroll operations as your business grows. Learn from companies that have successfully scaled.',
      image: 'scaling-operations',
      readTime: '7 min read'
    },
    {
      id: 5,
      title: 'Automating Payroll Workflows',
      author: 'Lisa Anderson',
      date: 'December 20, 2024',
      category: 'Automation',
      excerpt: 'Automation tips and tricks to save time and reduce errors in your payroll process.',
      image: 'automation',
      readTime: '5 min read'
    },
    {
      id: 6,
      title: 'Employee Data Security Best Practices',
      author: 'Robert Taylor',
      date: 'December 15, 2024',
      category: 'Security',
      excerpt: 'Comprehensive guide to securing employee data in your payroll system. Learn industry best practices.',
      image: 'security-practices',
      readTime: '9 min read'
    }
  ];

  const categories = ['All', 'Compliance', 'Management', 'Security', 'Growth', 'Automation'];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <motion.div
      className="blog-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
        <BlogHero />

      {/* Search & Filters */}
      <section className="blog-controls">
        <div className="container">
          <motion.div
            className="search-bar"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Search size={20} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </motion.div>

          <motion.div
            className="category-filters"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {categories.map((category, idx) => (
              <motion.button
                key={idx}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="blog-grid-section">
        <div className="container">
          {filteredPosts.length > 0 ? (
            <motion.div
              className="blog-grid"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {filteredPosts.map((post) => (
                <motion.article
                  key={post.id}
                  className="blog-card"
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                >
                  <div className="blog-image">
                    <div className="image-placeholder">
                      {post.category}
                    </div>
                  </div>

                  <div className="blog-content">
                    <div className="blog-meta">
                      <span className="category-tag">{post.category}</span>
                      <span className="read-time">{post.readTime}</span>
                    </div>

                    <h3>{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt}</p>

                    <div className="blog-footer">
                      <div className="blog-author-info">
                        <User size={16} />
                        <span>{post.author}</span>
                      </div>
                      <div className="blog-date">
                        <Calendar size={16} />
                        <span>{post.date}</span>
                      </div>
                    </div>

                    <Link to={`/blog/${post.id}`} className="read-more">
                      Read More <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          ) : (
            <motion.div
              className="no-results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3>No articles found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="blog-cta">
        <div className="blog-cta-bg"></div>
        <div className="container">
          <motion.div
            className="blog-cta-content"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Stay Updated</h2>
            <p>Subscribe to our newsletter for the latest payroll insights and updates</p>
            <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="your@email.com" required />
              <button type="submit" className="btn btn-primary">
                Subscribe
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Blog;